import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { OrderApi, OrderShipApi, ProductStateApi } from "../../../../../../../../../../services/Api/Organization/types";
import OrderShipBasicInfoForm from "./OrderShipBasicInfoForm";
import OrderShipCommentForm from "./OrderShipCommentForm";
import OrderShipItemsTableForm from "./OrderShipItemsTableForm";

interface Props {
	order: OrderApi;
	productStates: ProductStateApi[];
}

const OrderShipForm = ({ order, productStates }: Props) => {
	const form = useForm<OrderShipApi>({
		criteriaMode: "all",
		defaultValues: {
			items: order.items.map((item) => ({
				item_id: item.id,
				product: item.product,
				quantity_shipped: item.quantity_shipped ?? 0,
				quantity: item.quantity,
				quantity_to_ship: (item.quantity - (item.quantity_shipped ?? 0)).toFixed(5),
			})),
			shipped_at: order.ordered_at,
			comment: "",
			reference_number: "",
		},
	});
	const { formState, setError, handleSubmit } = form;
	const [loading, setLoading] = useState(false);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();
	const history = useHistory();
	const organization = useSelector(selectOrganization);

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) return;

		setLoading(true);
		try {
			await apiOrganization.createOrderShip(order.id, data);
			addSuccessFlash(t("lib:common.flash.saved"));
			history.push(`/${organization.id}/documents/sales/${order.id}`);
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty formState={formState} loading={loading} onSubmit={onSubmit}>
			<OrderShipBasicInfoForm form={form} setErrors={setErrors} />
			<OrderShipItemsTableForm form={form} productStates={productStates} />
			<OrderShipCommentForm form={form} />
		</FormDirty>
	);
};

export default OrderShipForm;
